import { useState, useEffect } from 'react';
import { Plus, Search, Trash2, Edit2, Loader2 } from 'lucide-react';
import { api } from '@/core/apiClient';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Company {
  id: number;
  name: string;
}

interface Brand {
  id: string;
  companyId: number;
  code: string;
  name: string;
  legalName?: string;
  cif?: string;
  address?: string;
  phone?: string;
  email?: string;
  primaryColor?: string;
  logoUrl?: string;
}

interface FormData {
  companyId: string;
  code: string;
  name: string;
  legalName: string;
  cif: string;
  address: string;
  phone: string;
  email: string;
  primaryColor: string;
  logoUrl: string;
}

const initialFormData: FormData = {
  companyId: '',
  code: '',
  name: '',
  legalName: '',
  cif: '',
  address: '',
  phone: '',
  email: '',
  primaryColor: '#000000',
  logoUrl: '',
};

export default function AdminBrands() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBrands();
    loadCompanies();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ success: boolean; data: Brand[] }>('/admin/brands');
      setBrands(response.data || []);
    } catch (err) {
      console.error('[AdminBrands] Error loading brands:', err);
      setError('Error en carregar les marques');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Company[] }>('/admin/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('[AdminBrands] Error loading companies:', err);
    }
  };

  const companyName = (companyId: number) => companies.find((c) => c.id === companyId)?.name || `ID: ${companyId}`;

  const filteredBrands = brands.filter(
    (b) =>
      b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.code?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const handleDelete = async (brandId: string) => {
    if (!window.confirm('Estàs segur que vols eliminar aquesta marca?')) return;

    try {
      const response = await api.delete<{ success: boolean }>(`/admin/brands/${brandId}`);
      if (response.success) {
        setBrands(brands.filter((b) => b.id !== brandId));
      }
    } catch (err) {
      console.error('[AdminBrands] Error deleting brand:', err);
      setError('Error en eliminar la marca');
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (b: Brand) => {
    setEditingId(b.id);
    setFormData({
      companyId: String(b.companyId),
      code: b.code,
      name: b.name,
      legalName: b.legalName || '',
      cif: b.cif || '',
      address: b.address || '',
      phone: b.phone || '',
      email: b.email || '',
      primaryColor: b.primaryColor || '#000000',
      logoUrl: b.logoUrl || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    try {
      if (editingId) {
        await api.put(`/admin/brands/${editingId}`, {
          name: formData.name,
          legalName: formData.legalName || undefined,
          cif: formData.cif || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          primaryColor: formData.primaryColor || undefined,
          logoUrl: formData.logoUrl || undefined,
        });
      } else {
        await api.post('/admin/brands', {
          companyId: parseInt(formData.companyId, 10),
          code: formData.code,
          name: formData.name,
          legalName: formData.legalName || undefined,
          cif: formData.cif || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          primaryColor: formData.primaryColor || undefined,
          logoUrl: formData.logoUrl || undefined,
        });
      }
      setShowModal(false);
      await loadBrands();
    } catch (err: any) {
      console.error('[AdminBrands] Error saving brand:', err);
      setFormError(err.response?.data?.message || 'Error en desar la marca');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Marques</h1>
          <p className="text-muted-foreground mt-1">Gestió de marques per empresa</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          <Plus className="w-4 h-4" />
          Nova marca
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Cercar per nom, codi..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Codi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Empresa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">CIF</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Email</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-foreground">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                    Carregant marques...
                  </td>
                </tr>
              ) : filteredBrands.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                    No hi ha marques per mostrar
                  </td>
                </tr>
              ) : (
                filteredBrands.map((b) => (
                  <tr key={b.id} className="border-b border-border hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded border border-border shrink-0"
                          style={{ backgroundColor: b.primaryColor || '#cccccc' }}
                        />
                        <span className="text-xs text-muted-foreground font-mono">{b.primaryColor || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-foreground">{b.name}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{b.code || '-'}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{companyName(b.companyId)}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{b.cif || '-'}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{b.email || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(b)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(b.id)}
                          className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">Total: {filteredBrands.length} marques</div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar marca' : 'Nova marca'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20 text-sm">
                {formError}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1">Empresa</label>
              <select
                required
                disabled={!!editingId}
                value={formData.companyId}
                onChange={(e) => setFormData((p) => ({ ...p, companyId: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Seleccionar —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Codi</label>
                <input
                  type="text"
                  required
                  disabled={!!editingId}
                  value={formData.code}
                  onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Nom</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Raó social</label>
              <input
                type="text"
                value={formData.legalName}
                onChange={(e) => setFormData((p) => ({ ...p, legalName: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">CIF</label>
                <input
                  type="text"
                  value={formData.cif}
                  onChange={(e) => setFormData((p) => ({ ...p, cif: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Telèfon</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Adreça</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Color de marca</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData((p) => ({ ...p, primaryColor: e.target.value }))}
                  className="h-10 w-14 cursor-pointer rounded border border-border bg-background p-1"
                />
                <span className="text-xs text-muted-foreground font-mono">{formData.primaryColor}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Logo (URL)</label>
              <input
                type="text"
                value={formData.logoUrl}
                onChange={(e) => setFormData((p) => ({ ...p, logoUrl: e.target.value }))}
                placeholder="https://.../logo.png"
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                S'usa com a logo als pressupostos d'aquesta marca. Si es deixa buit, s'usa el logo per defecte.
              </p>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel·lar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Desant...' : 'Desar'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
